import { BSLEmploymentStatus, Req } from '@cbo/shared-library';
import { MultiQueryParam, SingleQueryParam } from '@cbo/shared-library/request/firebase.request';
import { UserData } from '@cbo/shared-library/types/bslCore/provisioning';
import { useErrorHandler } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { validate as isValidUuid } from 'uuid';
import { isNil } from 'lodash';
import { downloadFile } from '../utilities/laborUtilities';
import routes from '../../constants/routes';
import { useSnackbar } from '../../contexts/SnackbarContext';
import useCallBslAndHandleErrors, { useUpdateUserStatusAndHandleErrors } from '../../lib/firebaseApiHandleErrors';
import useCallBsl from '../../utils/hooks/useCallBsl';

import { PaginationResponse } from '../../models/Paginated';
import { BaseReportDataRequest } from '../../../api/sales/requests';
import {
  ApproachingACAHourlyThresholdGridDataRequest,
  ApproachingACAHourlyThresholdGridDataResponse,
  ApproachingACAThresholdAverageHoursWorkedGridResponse,
  ChangeJobActivationStatusDto,
  CreateEmployeeRequestSchema,
  CreateEmployeeResponse,
  CreateSelfEmployeeRequestSchema,
  CreateSelfEmployeeResponse,
  Employee,
  EmployeeBreakReportCommonRequestSchema,
  EmployeeBreakReportGridDataResponse,
  EmployeeBreakReportKpiCardsDataResponse,
  EmployeeContactDetailsInformation,
  EmployeeDetailsInfo,
  EmployeePayrollDetailDataGridResponse,
  EmployeePayrollDto,
  EmployeePayrollGridResponse,
  EmployeePayrollKPICardData,
  EmployeeSalesPerformanceCommonRequest,
  EmployeeSalesPerformanceGridDataResponse,
  EmployeeSalesPerformanceKpiCardsResponse,
  EmployeeSitesGridRow,
  EmployeeTipKPICardsAndChartsData,
  EmployeeTipReportGridDataResponse,
  EmploymentHistoryIdResponse,
  GenericPayrollExportGridDataResponse,
  ExportFileReportGridResponse,
  GetEmployeesRequestSchema,
  GetJobProfilesRequestSchema,
  GetPaginatedJobsRequest,
  IntervalSalesAndLaborReportCommonRequestSchema,
  IntervalSalesAndLaborResponse,
  Job,
  JobcodeHistoryGridRequest,
  JobcodeHistoryGridResponse,
  JobProfile,
  LaborGroup,
  OvertimeThresholdCommonRequest,
  OvertimeThresholdGridDataResponse,
  OvertimeThresholdKpiCardsResponse,
  SelfJobProfile,
  UpdateEmployeeCertificationInfoSchema,
  UpdateEmployeeCertificationSchema,
  UpdateEmployeeContactInfoRequestSchema,
  UpdateEmployeeDetailsSchema,
  UpdateEmployeeHistorySchema,
  UpdateEmployeePersonalInfoRequestSchema,
  UpdateEmployeeRequestSchema,
  UpdateJobDto,
  UpdateJobProfileDto,
  UpdateResponse,
  UpdateUserStatusResponse,
  EmployeePayrollDetailExportDto,
  TipShareDistributionConfig,
  TipShareContributionConfig,
  SaveTipDistributionDto,
  SaveTipDistributionResponseDto,
  GetTipDistributionResponseDto,
  TipIncomeReportResponseDto,
  CalculateTipDistributionRequestDto,
  CalculateTipDistributionResponseDto,
  FinalizationRequestDto,
  FinalizationResponseDto,
  TipShareShift,
  CreateTipShareShiftDto,
  UpdateTipShareShiftDto,
  BannerStateQueryParams,
  BannerStateViewDto,
  DismissBannerDto,
  DismissBannerResponseDto,
} from '../types';
import { TipDistributionConfigResponseDto, ContributionPoolTotalsByShiftResponse } from '../TipShareDistribution/types';

import useCurrentSite from '../../utils/hooks/useCurrentSite';

export type LaborRequests = {
  getEmployee: (employeeId: string) => Promise<Employee | void>;
  getSelfEmployee: () => Promise<Employee | void>;
  updateSelfEmployeeInfo: (
    updateSelfEmployeeRequestSchema: UpdateEmployeeContactInfoRequestSchema | UpdateEmployeePersonalInfoRequestSchema
  ) => Promise<UpdateResponse | void>;
  createEmploymentHistory(
    employeeId: string,
    employmentHistory: UpdateEmployeeHistorySchema
  ): Promise<EmploymentHistoryIdResponse | void>;
  getJobcodeHistoryGridData: (body: JobcodeHistoryGridRequest) => Promise<JobcodeHistoryGridResponse | void>;
  getSelfEmployeeDetailsInfo: () => Promise<EmployeeDetailsInfo | void>;
  updateSelfEmployeeCertification: (
    certification: UpdateEmployeeCertificationInfoSchema,
    certificationId: string
  ) => Promise<UpdateResponse | void>;
  createSelfEmployeeCertification: (
    certification: UpdateEmployeeCertificationInfoSchema
  ) => Promise<UpdateResponse | void>;
  updateContactInformation: (
    employeeId: string,
    contactInformation: EmployeeContactDetailsInformation
  ) => Promise<UpdateResponse | void>;
  updateEmployeeDetails: (
    employeeId: string,
    employeeDetails: UpdateEmployeeDetailsSchema
  ) => Promise<UpdateResponse | void>;
  updateEmployeeCertification: (
    employeeId: string,
    certificationId: string,
    updateCertificationsDTO: UpdateEmployeeCertificationInfoSchema
  ) => Promise<UpdateResponse | void>;
  createEmployeeCertification: (
    employeeId: string,
    updateCertificationsDTO: UpdateEmployeeCertificationSchema
  ) => Promise<UpdateResponse | void>;
  getGenericPayrollExportGridData: (
    body: BaseReportDataRequest
  ) => Promise<GenericPayrollExportGridDataResponse[] | void>;
  exportGenericPayrollData: (body: BaseReportDataRequest) => Promise<ExportFileReportGridResponse | void>;
  getOvertimeThresholdReportGridData: (
    body: OvertimeThresholdCommonRequest
  ) => Promise<OvertimeThresholdGridDataResponse | void>;
  getOvertimeThresholdReportKpiCardsData: (
    body: OvertimeThresholdCommonRequest
  ) => Promise<OvertimeThresholdKpiCardsResponse | void>;
  getEmployeeSalesPerformanceReportGridData: (
    body: EmployeeSalesPerformanceCommonRequest
  ) => Promise<EmployeeSalesPerformanceGridDataResponse | void>;
  getEmployeeSalesPerformanceReportKpiCardsData: (
    body: EmployeeSalesPerformanceCommonRequest
  ) => Promise<EmployeeSalesPerformanceKpiCardsResponse | void>;
  getEmployeeTipReportGridData: (
    body: EmployeeBreakReportCommonRequestSchema
  ) => Promise<EmployeeTipReportGridDataResponse | void>;
  exportEmployeeTipReportGridData: (body: BaseReportDataRequest) => Promise<ExportFileReportGridResponse | void>;
  getEmployeeTipReportKpiCardsData: (
    body: EmployeeBreakReportCommonRequestSchema
  ) => Promise<EmployeeTipKPICardsAndChartsData | void>;
  getEmployeeBreakReportGridData: (
    body: EmployeeBreakReportCommonRequestSchema
  ) => Promise<EmployeeBreakReportGridDataResponse | void>;
  getEmployeeBreakReportKpiCardsData: (
    body: EmployeeBreakReportCommonRequestSchema
  ) => Promise<EmployeeBreakReportKpiCardsDataResponse | void>;
  getIntervalSalesAndLabor: (
    body: IntervalSalesAndLaborReportCommonRequestSchema
  ) => Promise<IntervalSalesAndLaborResponse | void>;
  getApproachingACAHourlyThresholdGridData: (
    body: ApproachingACAHourlyThresholdGridDataRequest
  ) => Promise<ApproachingACAHourlyThresholdGridDataResponse | void>;
  getAcaAverageHoursGridData: (
    body: BaseReportDataRequest
  ) => Promise<ApproachingACAThresholdAverageHoursWorkedGridResponse | void>;
  getEmployeePayrollGridData: (body: EmployeePayrollDto) => Promise<EmployeePayrollGridResponse | void>;
  exportEmployeePayrollGridData: (body: EmployeePayrollDto) => Promise<ExportFileReportGridResponse | void>;
  getEmployeePayrollKPICardsData: (body: EmployeePayrollDto) => Promise<EmployeePayrollKPICardData | void>;
  getEmployeePayrollDetailGridData: (body: EmployeePayrollDto) => Promise<EmployeePayrollDetailDataGridResponse | void>;
  exportEmployeePayrollDetailGridData: (
    body: EmployeePayrollDetailExportDto
  ) => Promise<ExportFileReportGridResponse | void>;
  getJobs: (query?: GetPaginatedJobsRequest) => Promise<PaginationResponse<Job> | void>;
  getJobById: (jobId: string) => Promise<Job | void>;
  updateJob: (jobId: string, job: UpdateJobDto) => Promise<void>;
  changeJobActivation: (jobId: string, changeJobActivationDto: ChangeJobActivationStatusDto) => Promise<void>;
  getLaborGroupByIdApi: (laborGroupId: string) => Promise<LaborGroup | void>;
  getJobProfilesByJobId: (jobId: string) => Promise<PaginationResponse<JobProfile> | void>;
  getEmployeeByEmployeeId: (
    employeeId: string,
    querySiteId?: string | null,
    fullEmploymentHistory?: boolean,
    isCfcOrg?: boolean
  ) => Promise<Employee | void>;
  getEmployees: (
    params?: GetEmployeesRequestSchema,
    excludeEUID?: boolean
  ) => Promise<PaginationResponse<Employee> | void>;
  getAsmEmployees: (params?: GetEmployeesRequestSchema) => Promise<PaginationResponse<Employee> | void>;
  updateEmployee: (employeeId: string, data: UpdateEmployeeRequestSchema) => Promise<UpdateResponse | void>;
  getJobProfilesByEmployeeId: (
    employeeId: string,
    includeHistory: boolean
  ) => Promise<PaginationResponse<JobProfile> | void>;
  getEmployeeSitesByEmployeeId: (employeeId: string) => Promise<PaginationResponse<EmployeeSitesGridRow> | void>;
  getLaborGroups: () => Promise<PaginationResponse<LaborGroup> | void>;
  getUrlWithEmployeeId: (id: string, querySiteId?: string | undefined) => Promise<string | undefined>;
  createJobProfile: (jobProfile: JobProfile) => Promise<string | void>;
  updateJobProfile: (jobProfileId: string, jobProfile: UpdateJobProfileDto) => Promise<string | void>;
  deleteJobProfile: (jobProfileId: string) => Promise<string | void>;
  getAllJobProfiles: (
    requestParameters?: GetJobProfilesRequestSchema
  ) => Promise<PaginationResponse<JobProfile> | void>;
  updateEmploymentHistory: (
    employeeId: string,
    employmentHistoryId: string,
    employmentHistory: UpdateEmployeeHistorySchema
  ) => Promise<void>;
  createSelfEmployee: (employee: CreateSelfEmployeeRequestSchema) => Promise<CreateSelfEmployeeResponse | void>;
  createEmployee: (employee: CreateEmployeeRequestSchema) => Promise<CreateEmployeeResponse | void>;
  getSelfJobProfile: () => Promise<SelfJobProfile[] | void>;
  updateUserStatus: (username: string, status: BSLEmploymentStatus) => Promise<UpdateUserStatusResponse | void>;
  getBspUser: (username: string) => Promise<UserData | void>;
  getTipShareDistributionConfig: () => Promise<TipShareDistributionConfig | null>;
  getTipShareContributionConfig: () => Promise<TipShareContributionConfig | null>;
  getContributionPoolTotals: (businessDate: string) => Promise<ContributionPoolTotalsByShiftResponse | void>;
  saveTipShareDistributionConfig: (data: TipShareDistributionConfig) => Promise<TipShareDistributionConfig | void>;
  updateTipShareDistributionConfig: (data: TipShareDistributionConfig) => Promise<TipShareDistributionConfig | void>;
  saveTipShareContributionConfig: (data: TipShareContributionConfig) => Promise<TipShareContributionConfig | void>;
  updateTipShareContributionConfig: (data: TipShareContributionConfig) => Promise<TipShareContributionConfig | void>;
  saveTipDistribution: (
    data: SaveTipDistributionDto,
    autoBalance?: boolean
  ) => Promise<SaveTipDistributionResponseDto | void>;
  getTipDistribution: (businessDate: string) => Promise<GetTipDistributionResponseDto | void>;
  getTipIncomeReport: (businessDate: string) => Promise<TipIncomeReportResponseDto | void>;
  calculateTipDistribution: (
    payload: CalculateTipDistributionRequestDto
  ) => Promise<CalculateTipDistributionResponseDto | void>;
  finalizeTipDistribution: (payload: FinalizationRequestDto) => Promise<FinalizationResponseDto | void>;
  unfinalizeTipDistribution: (payload: FinalizationRequestDto) => Promise<FinalizationResponseDto | void>;
  getTipConfiguration: () => Promise<TipDistributionConfigResponseDto[] | void>;
  listTipShareShifts: () => Promise<TipShareShift[] | void>;
  getTipShareShift: (shiftId: string) => Promise<TipShareShift | void>;
  createTipShareShift: (createDto: CreateTipShareShiftDto) => Promise<TipShareShift | void>;
  updateTipShareShift: (shiftId: string, updateDto: UpdateTipShareShiftDto) => Promise<TipShareShift | void>;
  deleteTipShareShift: (shiftId: string) => Promise<void>;
  getBannerState: (params: BannerStateQueryParams) => Promise<BannerStateViewDto | void>;
  dismissBanner: (body: DismissBannerDto) => Promise<DismissBannerResponseDto | void>;
};

const useLaborRequests = (): LaborRequests => {
  const handleError = useErrorHandler();
  const callBslAndHandleErrors = useCallBslAndHandleErrors();
  const callBsl = useCallBsl();
  const updateUserStatusAndHandleErrors = useUpdateUserStatusAndHandleErrors();
  const { siteId } = useCurrentSite();

  const proxies = {
    sales: 'sales',
    labor: 'labor',
    users: 'provisioning.users',
    tips: 'tips',
  };

  const Verb = Req.Firebase.HttpVerb;
  const { setSnackbarState } = useSnackbar();
  const { t } = useTranslation();
  /**
   * EMPLOYEE
   */

  const getEmployee = async (employeeId: string): Promise<Employee | void> =>
    callBslAndHandleErrors(
      {
        proxy: 'employees',
        verb: Verb.GET,
        pathSegments: ['employees', employeeId],
      },
      handleError
    );

  const getSelfEmployee = async (): Promise<Employee | void> =>
    callBslAndHandleErrors(
      {
        proxy: 'employees',
        verb: Verb.GET,
        pathSegments: ['employees', 'self'],
      },
      handleError
    );

  const updateContactInformation = async (
    employeeId: string,
    contactInformation: EmployeeContactDetailsInformation
  ): Promise<UpdateResponse | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.PUT,
        proxy: proxies.labor,
        pathSegments: ['employees', `${employeeId}`, 'contact'],
        payload: contactInformation,
      },
      handleError
    );

  const updateEmployeeDetails = async (
    employeeId: string,
    employeeDetails: UpdateEmployeeDetailsSchema
  ): Promise<UpdateResponse | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.PUT,
        proxy: proxies.labor,
        pathSegments: ['employees', `${employeeId}`],
        payload: employeeDetails,
      },
      handleError
    );

  /**
   * EMPLOYEE CONFIG
   */
  // helper method to get query params for get employees

  const addFilterAndSortParams = (
    queryParams: Array<Req.Firebase.MultiQueryParam | Req.Firebase.SingleQueryParam>,
    params?: GetEmployeesRequestSchema
  ): void => {
    if (params?.filter) {
      queryParams.push({ kind: 'multi', key: 'filter', value: params.filter });
    }
    if (params?.sortBy) {
      queryParams.push({ kind: 'single', key: 'sortBy', value: params.sortBy });
    }
    if (params?.sortOrder) {
      queryParams.push({ kind: 'single', key: 'sortOrder', value: params.sortOrder });
    }
  };

  const getEmployeesParams = (
    params?: GetEmployeesRequestSchema
  ): Array<Req.Firebase.MultiQueryParam | Req.Firebase.SingleQueryParam> => {
    const queryParams: Array<Req.Firebase.MultiQueryParam | Req.Firebase.SingleQueryParam> = [];

    if (params?.pagenum) {
      queryParams.push({ kind: 'single', key: 'pagenum', value: String(params.pagenum) });
    }
    if (params?.pagesize) {
      queryParams.push({ kind: 'single', key: 'pagesize', value: String(params.pagesize) });
    } else {
      queryParams.push({ kind: 'single', key: 'pagesize', value: '100' });
    }

    if (params?.employmentStatus) {
      queryParams.push({
        kind: 'single',
        key: 'employmentStatus',
        value: Array.isArray(params.employmentStatus) ? params.employmentStatus.join(',') : params.employmentStatus,
      });
    }
    if (params?.employeeIds) {
      const filteredEmployeeIds = params.employeeIds.filter((id) => id != null && id !== 'null');
      if (filteredEmployeeIds.length > 0) {
        queryParams.push({ kind: 'multi', key: 'employeeIds', value: filteredEmployeeIds });
      }
    }
    if (params?.posEmployeeIds) {
      queryParams.push({
        kind: 'single',
        key: 'posEmployeeIds',
        value: Array.isArray(params.posEmployeeIds) ? params.posEmployeeIds.join(',') : params.posEmployeeIds,
      });
    }
    if (params?.includeJobProfileCount) {
      queryParams.push({ kind: 'single', key: 'includeJobProfileCount', value: params.includeJobProfileCount });
    }
    if (params?.enterpriseUnit) {
      queryParams.push({ kind: 'single', key: 'enterpriseUnit', value: params.enterpriseUnit });
    }
    if (params?.fullEmploymentHistory) {
      queryParams.push({ kind: 'single', key: 'fullEmploymentHistory', value: String(!!params.fullEmploymentHistory) }); // double negation to convert to string and handle undefined
    }

    addFilterAndSortParams(queryParams, params);

    return queryParams;
  };

  const getJobProfilesParams = (
    params?: GetJobProfilesRequestSchema
  ): Array<Req.Firebase.MultiQueryParam | Req.Firebase.SingleQueryParam> => {
    const queryParams: Array<Req.Firebase.MultiQueryParam | Req.Firebase.SingleQueryParam> = [
      { kind: 'single', key: 'pagesize', value: '100' },
    ];

    if (params?.pagenum) {
      queryParams.push({ kind: 'single', key: 'pagenum', value: String(params.pagenum) });
    }

    if (params?.jobProfileIds) {
      queryParams.push({ kind: 'multi', key: 'jobProfileIds', value: params.jobProfileIds });
    }

    if (params?.includeHistory) {
      queryParams.push({ kind: 'single', key: 'includeHistory', value: String(params.includeHistory) });
    }

    return queryParams;
  };

  const getLaborGroupByIdApi = (laborGroupId: string): Promise<LaborGroup | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.GET,
        proxy: proxies.labor,
        pathSegments: ['labor-groups', laborGroupId],
      },
      handleError
    );

  /**
   * JOB CODES
   */

  const getJobcodeHistoryGridData = (body: JobcodeHistoryGridRequest): Promise<JobcodeHistoryGridResponse | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.POST,
        proxy: proxies.sales,
        pathSegments: ['jobcode-history', 'grid-data'],
        payload: body,
      },
      handleError
    );

  const getSelfEmployeeDetailsInfo = async (): Promise<EmployeeDetailsInfo | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.GET,
        proxy: proxies.labor,
        pathSegments: ['self', 'employee-details'],
      },
      handleError
    );

  /**
   * Self Update Employee Info
   */

  const updateSelfEmployeeInfo = async (
    updateSelfEmployeeRequestSchema: UpdateEmployeeContactInfoRequestSchema | UpdateEmployeePersonalInfoRequestSchema
  ): Promise<UpdateResponse | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.PUT,
        proxy: proxies.labor,
        pathSegments: ['self', 'employee-details'],
        payload: updateSelfEmployeeRequestSchema,
      },
      handleError
    );

  /**
   * CERTIFICATIONS
   */

  const updateEmployeeCertification = async (
    employeeId: string,
    certificationId: string,
    updateCertificationsDTO: UpdateEmployeeCertificationInfoSchema
  ): Promise<UpdateResponse | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.PUT,
        proxy: proxies.labor,
        pathSegments: ['employees', `${employeeId}`, 'certifications', `${certificationId}`],
        payload: updateCertificationsDTO,
      },
      handleError
    );

  const createEmployeeCertification = async (
    employeeId: string,
    updateCertificationsDTO: UpdateEmployeeCertificationInfoSchema
  ): Promise<UpdateResponse | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.POST,
        proxy: proxies.labor,
        pathSegments: ['employees', `${employeeId}`, 'certifications'],
        payload: updateCertificationsDTO,
      },
      handleError
    );

  const updateSelfEmployeeCertification = async (
    certification: UpdateEmployeeCertificationInfoSchema,
    certificationId: string
  ): Promise<UpdateResponse | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.PUT,
        proxy: proxies.labor,
        pathSegments: ['self', 'certifications', `${certificationId}`],
        payload: certification,
      },
      handleError
    );
  const createSelfEmployeeCertification = async (
    certification: UpdateEmployeeCertificationInfoSchema
  ): Promise<UpdateResponse | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.POST,
        proxy: proxies.labor,
        pathSegments: ['self', 'certifications'],
        payload: certification,
      },
      handleError
    );

  /**
   * EMPLOYEE SALES PERFORMANCE
   */

  const getEmployeeSalesPerformanceReportGridData = async (
    body: EmployeeSalesPerformanceCommonRequest
  ): Promise<EmployeeSalesPerformanceGridDataResponse | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.POST,
        proxy: proxies.sales,
        pathSegments: ['employee-sales', 'grid-data'],
        payload: body,
      },
      handleError
    );

  const getEmployeeSalesPerformanceReportKpiCardsData = async (
    body: EmployeeSalesPerformanceCommonRequest
  ): Promise<EmployeeSalesPerformanceKpiCardsResponse | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.POST,
        proxy: proxies.sales,
        pathSegments: ['employee-sales', 'kpi-cards'],
        payload: body,
      },
      handleError
    );

  /**
   * APPROACHING OT THRESHOLD
   */

  const getOvertimeThresholdReportGridData = async (
    body: OvertimeThresholdCommonRequest
  ): Promise<OvertimeThresholdGridDataResponse | void> =>
    callBslAndHandleErrors(
      {
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['approaching-overtime-threshold', 'grid-data'],
        payload: body,
      },
      handleError
    );

  const getOvertimeThresholdReportKpiCardsData = async (
    body: OvertimeThresholdCommonRequest
  ): Promise<OvertimeThresholdKpiCardsResponse | void> =>
    callBslAndHandleErrors(
      {
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['approaching-overtime-threshold', 'kpi-cards'],
        payload: body,
      },
      handleError
    );

  /**
   * GENERIC PAYROLL EXPORT
   */
  const getGenericPayrollExportGridData = async (
    body: BaseReportDataRequest
  ): Promise<GenericPayrollExportGridDataResponse[] | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.POST,
        proxy: proxies.sales,
        pathSegments: ['generic-payroll-export'],
        payload: body,
      },
      handleError
    );

  const exportGenericPayrollData = async (
    body: BaseReportDataRequest
  ): Promise<ExportFileReportGridResponse | void> => {
    const data: ExportFileReportGridResponse = await callBsl({
      verb: Verb.POST,
      proxy: proxies.sales,
      pathSegments: ['generic-payroll-export', 'export'],
      payload: body,
    });
    if (data.signedUrl) {
      downloadFile(data.signedUrl);
    }
    return data;
  };
  /**
   * EMPLOYEE TIP REPORT
   */

  const getEmployeeTipReportGridData = async (
    body: EmployeeBreakReportCommonRequestSchema
  ): Promise<EmployeeTipReportGridDataResponse | void> =>
    callBslAndHandleErrors(
      { verb: Verb.POST, proxy: proxies.sales, pathSegments: ['employee-tip', 'grid-data'], payload: body },
      handleError
    );

  const getEmployeeTipReportKpiCardsData = async (
    body: EmployeeBreakReportCommonRequestSchema
  ): Promise<EmployeeTipKPICardsAndChartsData | void> =>
    callBslAndHandleErrors(
      { verb: Verb.POST, proxy: proxies.sales, pathSegments: ['employee-tip', 'kpi-cards'], payload: body },
      handleError
    );

  const exportEmployeeTipReportGridData = async (
    body: BaseReportDataRequest
  ): Promise<ExportFileReportGridResponse | void> => {
    const data: ExportFileReportGridResponse | void = await callBslAndHandleErrors(
      {
        verb: Verb.POST,
        proxy: proxies.sales,
        pathSegments: ['employee-tip', 'grid-data', 'export'],
        payload: body,
      },
      handleError
    );
    if (data?.signedUrl) {
      downloadFile(data?.signedUrl);
    }
    return data;
  };
  /**
   * EMPLOYEE BREAK REPORT
   */
  const getEmployeeBreakReportGridData = async (
    body: EmployeeBreakReportCommonRequestSchema
  ): Promise<EmployeeBreakReportGridDataResponse | void> =>
    callBslAndHandleErrors(
      { verb: Verb.POST, proxy: proxies.sales, pathSegments: ['employee-break', 'grid-data'], payload: body },
      handleError
    );

  const getEmployeeBreakReportKpiCardsData = async (
    body: EmployeeBreakReportCommonRequestSchema
  ): Promise<EmployeeBreakReportKpiCardsDataResponse | void> =>
    callBslAndHandleErrors(
      { verb: Verb.POST, proxy: proxies.sales, pathSegments: ['employee-break', 'kpi-cards'], payload: body },
      handleError
    );

  /**
   * INTERVAL SALES AND LABOR REPORT
   */
  const getIntervalSalesAndLabor = async (
    body: IntervalSalesAndLaborReportCommonRequestSchema
  ): Promise<IntervalSalesAndLaborResponse | void> =>
    callBslAndHandleErrors(
      { verb: Verb.POST, proxy: proxies.sales, pathSegments: ['interval-sales-labor'], payload: body },
      handleError
    );

  /**
   * APPROACHING ACA THRESHOLD
   */

  const getApproachingACAHourlyThresholdGridData = async (
    body: ApproachingACAHourlyThresholdGridDataRequest
  ): Promise<ApproachingACAHourlyThresholdGridDataResponse | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.POST,
        proxy: proxies.sales,
        pathSegments: ['approaching-aca-threshold', 'hourly-threshold', 'grid-data'],
        payload: body,
      },
      handleError
    );

  const getAcaAverageHoursGridData = async (
    body: BaseReportDataRequest
  ): Promise<ApproachingACAThresholdAverageHoursWorkedGridResponse | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.POST,
        proxy: proxies.sales,
        pathSegments: ['approaching-aca-threshold', 'employee-average', 'grid-data'],
        payload: body,
      },
      handleError
    );

  /**
   * Employee Payroll Report
   */

  const getEmployeePayrollDetailGridData = async (
    body: EmployeePayrollDto
  ): Promise<EmployeePayrollDetailDataGridResponse | void> =>
    callBslAndHandleErrors(
      {
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['employee-payroll', 'detail', 'grid-data'],
        payload: body,
      },
      handleError
    );

  const exportEmployeePayrollDetailGridData = async (
    body: EmployeePayrollDetailExportDto
  ): Promise<ExportFileReportGridResponse | void> => {
    const data: ExportFileReportGridResponse | void = await callBslAndHandleErrors(
      {
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['employee-payroll', 'detail', 'grid-data', 'export'],
        payload: body,
      },
      handleError
    );
    if (data?.signedUrl) {
      downloadFile(data?.signedUrl);
    }
    return data;
  };

  const getEmployeePayrollKPICardsData = async (body: EmployeePayrollDto): Promise<EmployeePayrollKPICardData | void> =>
    callBslAndHandleErrors(
      {
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['employee-payroll', 'kpi-cards'],
        payload: body,
      },
      handleError
    );

  const exportEmployeePayrollGridData = async (
    body: EmployeePayrollDto
  ): Promise<ExportFileReportGridResponse | void> => {
    const data: ExportFileReportGridResponse | void = await callBslAndHandleErrors(
      {
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['employee-payroll', 'summary', 'grid-data', 'export'],
        payload: body,
      },
      handleError
    );
    if (data?.signedUrl) {
      downloadFile(data?.signedUrl);
    }
    return data;
  };

  const getEmployeePayrollGridData = async (body: EmployeePayrollDto): Promise<EmployeePayrollGridResponse | void> =>
    callBslAndHandleErrors(
      {
        proxy: proxies.sales,
        verb: Verb.POST,
        pathSegments: ['employee-payroll', 'summary', 'grid-data'],
        payload: body,
      },
      handleError
    );

  const getJobs = async (query?: GetPaginatedJobsRequest): Promise<PaginationResponse<Job> | void> => {
    const queryParams: Array<MultiQueryParam | SingleQueryParam> = [];

    if (query?.ids?.length) {
      queryParams.push({ kind: 'multi', key: 'ids[]', value: query?.ids });
    }

    if (query?.pagenum) {
      queryParams.push({ kind: 'single', key: 'pagenum', value: String(query?.pagenum) });
    }

    if (query?.includeJobProfileCount) {
      queryParams.push({ kind: 'single', key: 'includeJobProfileCount', value: 'true' });
    }

    if (query?.enterpriseUnit) {
      queryParams.push({ kind: 'single', key: 'enterpriseUnit', value: query.enterpriseUnit });
    }

    return callBslAndHandleErrors(
      {
        verb: Verb.GET,
        proxy: proxies.labor,
        pathSegments: ['jobs'],
        queryParams: [
          ...queryParams,
          // TODO: Setting this to max page size allowed for now. Update once pagination is completely implemented.
          { kind: 'single', key: 'pagesize', value: '100' },
        ],
      },
      handleError
    );
  };

  const getJobById = async (jobId: string): Promise<Job | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.GET,
        proxy: proxies.labor,
        pathSegments: ['jobs', jobId],
        queryParams: [{ kind: 'single', key: 'includeJobProfileCount', value: 'true' }],
      },
      handleError
    );

  const updateJob = async (jobId: string, job: UpdateJobDto): Promise<void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.PUT,
        proxy: proxies.labor,
        pathSegments: ['jobs', jobId],
        payload: job,
      },
      handleError
    );

  const changeJobActivation = async (
    jobid: string,
    changeJobActivationDto: ChangeJobActivationStatusDto
  ): Promise<void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.PATCH,
        proxy: proxies.labor,
        pathSegments: ['jobs', jobid, 'changeActivation'],
        payload: changeJobActivationDto,
      },
      handleError
    );

  const getEmployees = async (
    params?: GetEmployeesRequestSchema,
    excludeEUID?: boolean
  ): Promise<PaginationResponse<Employee> | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.GET,
        proxy: proxies.labor,
        pathSegments: ['employees'],
        queryParams: excludeEUID
          ? getEmployeesParams({ ...params })
          : getEmployeesParams({ ...params, enterpriseUnit: siteId }),
      },
      handleError
    );

  const getAsmEmployees = async (params?: GetEmployeesRequestSchema): Promise<PaginationResponse<Employee> | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.GET,
        proxy: proxies.labor,
        pathSegments: ['employees', 'users-at-asm'],
        queryParams: getEmployeesParams({ ...params }),
      },
      handleError
    );

  const getJobProfilesByJobId = async (jobId: string): Promise<PaginationResponse<JobProfile> | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.GET,
        proxy: proxies.labor,
        pathSegments: ['jobs', jobId, 'jobProfiles'],
        queryParams: [
          { kind: 'single', key: 'jobId', value: jobId },
          { kind: 'single', key: 'pagesize', value: '100' },
        ],
      },
      handleError
    );

  const getEmployeeByEmployeeId = async (
    employeeId: string,
    querySiteId?: string | null,
    fullEmploymentHistory?: boolean,
    isCfcOrg?: boolean
  ): Promise<Employee | void> => {
    const queryParams: Array<SingleQueryParam | MultiQueryParam> = [
      { kind: 'single', key: 'fullEmploymentHistory', value: String(!!fullEmploymentHistory) },
    ];

    if (!isCfcOrg) {
      const isQuerySiteIdEmpty = isNil(querySiteId) || querySiteId === '';
      const isSiteIdEmpty = isNil(siteId) || siteId === '';
      // Only add enterpriseUnit if either is NOT empty
      if (!isQuerySiteIdEmpty || !isSiteIdEmpty) {
        const effectiveSiteId = isQuerySiteIdEmpty ? siteId : querySiteId;
        queryParams.push({ kind: 'single', key: 'enterpriseUnit', value: effectiveSiteId });
      }
    }

    return callBslAndHandleErrors(
      {
        verb: Verb.GET,
        proxy: proxies.labor,
        pathSegments: ['employees', employeeId],
        queryParams,
      },
      handleError
    );
  };

  const updateEmployee = async (
    employeeId: string,
    data: UpdateEmployeeRequestSchema
  ): Promise<UpdateResponse | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.PUT,
        proxy: proxies.labor,
        pathSegments: ['employees', employeeId],
        payload: data,
      },
      handleError
    );

  const getJobProfilesByEmployeeId = async (
    employeeId: string,
    includeHistory?: boolean
  ): Promise<PaginationResponse<JobProfile> | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.GET,
        proxy: proxies.labor,
        pathSegments: ['employees', employeeId, 'job-profiles'],
        queryParams: [
          { kind: 'single', key: 'includeHistory', value: (includeHistory ?? false).toString() },
          { kind: 'single', key: 'pagesize', value: '100' },
        ],
      },
      handleError
    );

  const getEmployeeSitesByEmployeeId = async (
    employeeId: string
  ): Promise<PaginationResponse<EmployeeSitesGridRow> | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.GET,
        proxy: proxies.labor,
        pathSegments: ['employees', employeeId, 'effective-grants'],
        queryParams: [{ kind: 'single', key: 'pagesize', value: '100' }],
      },
      handleError
    );

  const getLaborGroups = (): Promise<PaginationResponse<LaborGroup> | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.GET,
        proxy: proxies.labor,
        pathSegments: ['labor-groups'],
      },
      handleError
    );

  const getUrlWithEmployeeId = (id: string, querySiteId?: string): Promise<string | undefined> =>
    callBslAndHandleErrors<PaginationResponse<Employee>, void>(
      {
        proxy: proxies.labor,
        verb: Verb.GET,
        pathSegments: ['employees'],
        queryParams: isValidUuid(id)
          ? [
              {
                kind: 'multi',
                key: 'employeeIds',
                value: [id.toString()],
              },
            ]
          : [
              {
                kind: 'single',
                key: 'posEmployeeId',
                value: id.toString(),
              },
            ],
      },
      handleError
    )
      .then((res: PaginationResponse<Employee> | void) => {
        const castRes = res as PaginationResponse<Employee>;
        if (castRes.pageContent.length === 0) {
          setSnackbarState({
            open: true,
            message: t('errors.employeeNotFound'),
            autoHideDuration: 2000,
            color: 'error',
          });
          return '';
        }
        const { employeeId } = castRes.pageContent[0];
        const queryParams = querySiteId ? `?site=${querySiteId}` : '';
        const route = routes.EMPLOYEE_GENERAL_INFORMATION_PAGE.replace(':employeeId', employeeId);
        return window.location.origin + route + queryParams;
      })
      .catch((err) => {
        setSnackbarState({
          open: true,
          message: t('sales.errors.genericMessage'),
          color: 'error',
        });
        return err;
      });

  const createEmploymentHistory = async (
    employeeId: string,
    employmentHistory: UpdateEmployeeHistorySchema
  ): Promise<EmploymentHistoryIdResponse | void> => {
    callBslAndHandleErrors(
      {
        verb: Verb.POST,
        proxy: proxies.labor,
        pathSegments: ['employees', `${employeeId}`, 'employment-history'],
        payload: employmentHistory,
      },
      handleError
    );
  };

  const updateEmploymentHistory = async (
    employeeId: string,
    employmentHistoryId: string,
    employmentHistory: UpdateEmployeeHistorySchema
  ): Promise<void> => {
    callBslAndHandleErrors(
      {
        verb: Verb.PUT,
        proxy: proxies.labor,
        pathSegments: ['employees', employeeId, 'employment-history', employmentHistoryId],
        payload: employmentHistory,
      },
      handleError
    );
  };

  const createJobProfile = async (jobProfile: JobProfile): Promise<string | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.POST,
        proxy: proxies.labor,
        pathSegments: ['job-profiles'],
        payload: jobProfile,
      },
      handleError
    );

  const updateJobProfile = async (jobProfileId: string, jobProfile: UpdateJobProfileDto): Promise<void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.PUT,
        proxy: proxies.labor,
        pathSegments: ['job-profiles', jobProfileId],
        payload: jobProfile,
      },
      handleError
    );

  const deleteJobProfile = async (jobProfileId: string): Promise<string | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.DELETE,
        proxy: proxies.labor,
        pathSegments: ['job-profiles', jobProfileId],
      },
      handleError
    );

  const getAllJobProfiles = async (
    pageParam?: GetJobProfilesRequestSchema
  ): Promise<PaginationResponse<JobProfile> | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.GET,
        proxy: proxies.labor,
        pathSegments: ['job-profiles'],
        queryParams: getJobProfilesParams(pageParam),
      },
      handleError
    );

  const createSelfEmployee = async (
    employee: CreateSelfEmployeeRequestSchema
  ): Promise<CreateSelfEmployeeResponse | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.POST,
        proxy: proxies.labor,
        pathSegments: ['self', 'employee'],
        payload: employee,
      },
      handleError
    );

  const createEmployee = async (employee: CreateEmployeeRequestSchema): Promise<CreateEmployeeResponse | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.POST,
        proxy: proxies.labor,
        pathSegments: ['employees'],
        payload: employee,
      },
      handleError
    );

  const getSelfJobProfile = async (): Promise<SelfJobProfile[] | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.GET,
        proxy: proxies.labor,
        pathSegments: ['self', 'job-profiles'],
      },
      handleError
    );

  const updateUserStatus = async (
    userName: string,
    status: BSLEmploymentStatus
  ): Promise<UpdateUserStatusResponse | void> =>
    updateUserStatusAndHandleErrors(
      {
        verb: Verb.PUT,
        proxy: proxies.users,
        pathSegments: [userName, 'status'],
        payload: { status },
      },
      handleError
    );

  const getBspUser = async (userName: string): Promise<UserData | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.GET,
        proxy: proxies.users,
        pathSegments: [userName],
      },
      handleError
    );

  const getTipShareDistributionConfig = async (): Promise<TipShareDistributionConfig | null> => {
    try {
      const response = await callBsl<TipShareDistributionConfig[]>({
        proxy: proxies.tips,
        verb: Verb.GET,
        pathSegments: ['config', 'distribution'],
      });
      // Extract the first item from the array, return null if empty
      return response?.[0] || null;
    } catch (error) {
      handleError(error);
      return null; // Return null instead of undefined for React Query
    }
  };

  const getTipShareContributionConfig = async (): Promise<TipShareContributionConfig | null> => {
    try {
      const response = await callBsl<TipShareContributionConfig[]>({
        proxy: proxies.tips,
        verb: Verb.GET,
        pathSegments: ['config', 'contribution'],
      });
      // Extract the first item from the array, return null if empty
      return response?.[0] || null;
    } catch (error) {
      handleError(error);
      return null; // Return null instead of undefined for React Query
    }
  };

  const getContributionPoolTotals = async (
    businessDate: string
  ): Promise<ContributionPoolTotalsByShiftResponse | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.GET,
        proxy: proxies.tips,
        pathSegments: ['contribution', 'pool-total'],
        enterpriseUnit: siteId,
        queryParams: [{ kind: 'single', key: 'businessDate', value: businessDate }],
      },
      handleError
    );

  const saveTipShareDistributionConfig = async (
    data: TipShareDistributionConfig
  ): Promise<TipShareDistributionConfig | void> =>
    callBsl<TipShareDistributionConfig>({
      proxy: proxies.tips,
      verb: Verb.POST,
      pathSegments: ['config', 'distribution'],
      enterpriseUnit: siteId,
      payload: data,
    }).catch(handleError);

  const updateTipShareDistributionConfig = async (
    data: TipShareDistributionConfig
  ): Promise<TipShareDistributionConfig | void> => {
    const pathSegments = data.id ? ['config', 'distribution', data.id] : ['config', 'distribution'];

    // Extract id from data and send only the fields to update in payload
    const { id, ...payloadWithoutId } = data;

    return callBsl<TipShareDistributionConfig>({
      proxy: proxies.tips,
      verb: Verb.PATCH,
      pathSegments,
      enterpriseUnit: siteId,
      payload: payloadWithoutId,
    }).catch(handleError);
  };

  const saveTipShareContributionConfig = async (
    data: TipShareContributionConfig
  ): Promise<TipShareContributionConfig | void> =>
    callBsl<TipShareContributionConfig>({
      proxy: proxies.tips,
      verb: Verb.POST,
      pathSegments: ['config', 'contribution'],
      enterpriseUnit: siteId,
      payload: data,
    }).catch(handleError);

  const updateTipShareContributionConfig = async (
    data: TipShareContributionConfig
  ): Promise<TipShareContributionConfig | void> => {
    const pathSegments = data.id ? ['config', 'contribution', data.id] : ['config', 'contribution'];

    // Extract id from data and send only the fields to update in payload
    const { id, ...payloadWithoutId } = data;

    return callBsl<TipShareContributionConfig>({
      proxy: proxies.tips,
      verb: Verb.PATCH,
      pathSegments,
      enterpriseUnit: siteId,
      payload: payloadWithoutId,
    }).catch(handleError);
  };

  const saveTipDistribution = async (
    data: SaveTipDistributionDto,
    autoBalance?: boolean
  ): Promise<SaveTipDistributionResponseDto | void> => {
    const queryParams: Array<Req.Firebase.SingleQueryParam> = [];
    if (typeof autoBalance === 'boolean') {
      queryParams.push({ kind: 'single', key: 'autoBalance', value: String(autoBalance) });
    }

    return callBslAndHandleErrors(
      {
        verb: Verb.POST,
        proxy: proxies.tips,
        pathSegments: ['distribute', 'save'],
        payload: data,
        enterpriseUnit: siteId,
        queryParams,
      },
      handleError
    );
  };

  const getTipDistribution = async (businessDate: string): Promise<GetTipDistributionResponseDto | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.GET,
        proxy: proxies.tips,
        pathSegments: ['distribute', businessDate],
        enterpriseUnit: siteId,
      },
      handleError
    );

  const getTipIncomeReport = async (businessDate: string): Promise<TipIncomeReportResponseDto | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.GET,
        proxy: proxies.tips,
        pathSegments: ['distribute', 'tip-income-report', businessDate],
        enterpriseUnit: siteId,
      },
      handleError
    );

  const calculateTipDistribution = async (
    payload: CalculateTipDistributionRequestDto
  ): Promise<CalculateTipDistributionResponseDto | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.POST,
        proxy: proxies.tips,
        pathSegments: ['distribute', 'calculate'],
        payload,
        enterpriseUnit: siteId,
      },
      handleError
    );

  const finalizeTipDistribution = async (payload: FinalizationRequestDto): Promise<FinalizationResponseDto | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.POST,
        proxy: proxies.tips,
        pathSegments: ['distribute', 'finalize'],
        payload,
        enterpriseUnit: siteId,
      },
      handleError
    );

  const unfinalizeTipDistribution = async (payload: FinalizationRequestDto): Promise<FinalizationResponseDto | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.POST,
        proxy: proxies.tips,
        pathSegments: ['distribute', 'unfinalize'],
        payload,
        enterpriseUnit: siteId,
      },
      handleError
    );

  const getTipConfiguration = async (): Promise<TipDistributionConfigResponseDto[] | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.GET,
        proxy: proxies.tips,
        pathSegments: ['config', 'distribution'],
        enterpriseUnit: siteId,
      },
      handleError
    );

  const listTipShareShifts = async (): Promise<TipShareShift[] | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.GET,
        proxy: proxies.tips,
        pathSegments: ['shifts'],
      },
      handleError
    );

  const getTipShareShift = async (shiftId: string): Promise<TipShareShift | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.GET,
        proxy: proxies.tips,
        pathSegments: ['shifts', shiftId],
        queryParams: siteId ? [{ kind: 'single', key: 'nepEnterpriseUnit', value: siteId }] : [],
      },
      handleError
    );

  const createTipShareShift = async (createDto: CreateTipShareShiftDto): Promise<TipShareShift | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.POST,
        proxy: proxies.tips,
        pathSegments: ['shifts'],
        queryParams: siteId ? [{ kind: 'single', key: 'nepEnterpriseUnit', value: siteId }] : [],
        payload: createDto,
      },
      handleError
    );

  const updateTipShareShift = async (
    shiftId: string,
    updateDto: UpdateTipShareShiftDto
  ): Promise<TipShareShift | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.PATCH,
        proxy: proxies.tips,
        pathSegments: ['shifts', shiftId],
        queryParams: siteId ? [{ kind: 'single', key: 'nepEnterpriseUnit', value: siteId }] : [],
        payload: updateDto,
      },
      handleError
    );

  const deleteTipShareShift = async (shiftId: string): Promise<void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.DELETE,
        proxy: proxies.tips,
        pathSegments: ['shifts', shiftId],
        queryParams: siteId ? [{ kind: 'single', key: 'nepEnterpriseUnit', value: siteId }] : [],
      },
      handleError
    );

  /**
   * BANNER STATE
   */

  const getBannerState = async (params: BannerStateQueryParams): Promise<BannerStateViewDto | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.GET,
        proxy: proxies.labor,
        pathSegments: ['banner-state'],
        queryParams: [
          { kind: 'single', key: 'entityType', value: params.entityType },
          { kind: 'single', key: 'entityId', value: params.entityId },
          { kind: 'single', key: 'bannerType', value: params.bannerType },
        ],
      },
      handleError
    );

  const dismissBanner = async (body: DismissBannerDto): Promise<DismissBannerResponseDto | void> =>
    callBslAndHandleErrors(
      {
        verb: Verb.POST,
        proxy: proxies.labor,
        pathSegments: ['banner-state', 'dismiss'],
        payload: body,
      },
      handleError
    );

  return {
    getEmployee,
    createEmploymentHistory,
    getSelfEmployee,
    getJobcodeHistoryGridData,
    getEmployeeByEmployeeId,
    getSelfEmployeeDetailsInfo,
    updateSelfEmployeeCertification,
    createSelfEmployeeCertification,
    updateContactInformation,
    updateEmployeeDetails,
    updateEmployeeCertification,
    createEmployeeCertification,
    getGenericPayrollExportGridData,
    exportGenericPayrollData,
    getOvertimeThresholdReportGridData,
    getOvertimeThresholdReportKpiCardsData,
    getEmployeeTipReportGridData,
    exportEmployeeTipReportGridData,
    getEmployeeTipReportKpiCardsData,
    getEmployeeBreakReportGridData,
    getEmployeeBreakReportKpiCardsData,
    getIntervalSalesAndLabor,
    getEmployeeSalesPerformanceReportGridData,
    getEmployeeSalesPerformanceReportKpiCardsData,
    getApproachingACAHourlyThresholdGridData,
    getAcaAverageHoursGridData,
    getEmployeePayrollDetailGridData,
    exportEmployeePayrollDetailGridData,
    getEmployeePayrollKPICardsData,
    getEmployeePayrollGridData,
    exportEmployeePayrollGridData,
    getJobs,
    getJobById,
    updateJob,
    changeJobActivation,
    getLaborGroupByIdApi,
    getJobProfilesByJobId,
    getEmployees,
    getAsmEmployees,
    updateEmployee,
    getJobProfilesByEmployeeId,
    getEmployeeSitesByEmployeeId,
    updateSelfEmployeeInfo,
    getLaborGroups,
    getUrlWithEmployeeId,
    createJobProfile,
    updateJobProfile,
    deleteJobProfile,
    getAllJobProfiles,
    updateEmploymentHistory,
    createSelfEmployee,
    createEmployee,
    getSelfJobProfile,
    updateUserStatus,
    getBspUser,
    getTipShareDistributionConfig,
    getTipShareContributionConfig,
    getContributionPoolTotals,
    saveTipShareDistributionConfig,
    updateTipShareDistributionConfig,
    saveTipShareContributionConfig,
    updateTipShareContributionConfig,
    saveTipDistribution,
    getTipDistribution,
    getTipIncomeReport,
    calculateTipDistribution,
    finalizeTipDistribution,
    unfinalizeTipDistribution,
    getTipConfiguration,
    listTipShareShifts,
    getTipShareShift,
    createTipShareShift,
    updateTipShareShift,
    deleteTipShareShift,
    getBannerState,
    dismissBanner,
  };
};

export default useLaborRequests;
